import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { supabase } from '../supabaseClient';
import { fetchAllTablesWithVenueId, fetchFloors, fetchTablesWithFloor } from '../utils/dataService';
import { useOrdersContext } from './OrdersContext';
import { reorderVenueTables } from '../utils/staffApiService';
import { decorateTablesWithOperationalState } from '../utils/tableDisplayState';

const TablesContext = createContext();

export const TablesProvider = ({ children, venueId }) => {
  const [tables, setTables] = useState([]);
  const [allTables, setAllTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [floors, setFloors] = useState([]);
  const [currentFloor, setCurrentFloor] = useState('');
  const [selectedTable, setSelectedTable] = useState(null);
  const [orders, setOrders] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const { allOrders } = useOrdersContext();
  const { getAccessTokenSilently } = useAuth0();

  const handleTableClick = (table) => {
    setSelectedTable(table);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTable(null);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedTables = Array.from(tables);
    const [movedTable] = reorderedTables.splice(result.source.index, 1);
    reorderedTables.splice(result.destination.index, 0, movedTable);

    setTables(reorderedTables);
    try {
      await reorderVenueTables(venueId, reorderedTables, getAccessTokenSilently);
    } catch (error) {
      console.error('Error reordering tables:', error.message);
    }
  };

  const decorateTables = useMemo(
    () => (tableRows = []) => decorateTablesWithOperationalState(tableRows, allOrders),
    [allOrders]
  );

  useEffect(() => {
    const initializeFloors = async () => {
      const fetchedFloors = await fetchFloors(venueId);
      setFloors(fetchedFloors);
      if (fetchedFloors.length > 0) {
        setCurrentFloor(fetchedFloors[0]);
      }
    };

    initializeFloors();
  }, [venueId]);

  useEffect(() => {
    const initializeTables = async () => {
      if (!currentFloor) {
        return;
      }

      setLoading(true);
      const fetchedTables = await fetchTablesWithFloor(venueId, currentFloor.id);
      setTables(fetchedTables);
      setLoading(false);
    };

    initializeTables();
  }, [currentFloor, venueId]);

  useEffect(() => {
    const initializeAllTables = async () => {
      setLoading(true);
      const fetchedTables = await fetchAllTablesWithVenueId(venueId);
      setAllTables(fetchedTables);
      setLoading(false);
    };

    initializeAllTables();
  }, [venueId]);

  useEffect(() => {
    if (selectedTable) {
      const filteredOrders = allOrders.filter(
        (order) => order.table_id === selectedTable.id && order.payment_status !== 'paid'
      );
      setOrders(filteredOrders);
    } else {
      setOrders([]);
    }
  }, [selectedTable, allOrders]);

  useEffect(() => {
    const tablesChannel = supabase
      .channel('public:tables')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tables' }, (payload) => {
        if (payload.new.venue_id === venueId) {
          setTables((prevTables) => [...prevTables, payload.new]);
          setAllTables((prevTables) => [...prevTables, payload.new]);
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tables' }, (payload) => {
        if (payload.new.venue_id === venueId) {
          setTables((prevTables) =>
            prevTables.map((table) => (table.id === payload.new.id ? payload.new : table))
          );
          setAllTables((prevTables) =>
            prevTables.map((table) => (table.id === payload.new.id ? payload.new : table))
          );
        }
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'tables' }, (payload) => {
        if (payload.old.venue_id === venueId) {
          setTables((prevTables) => prevTables.filter((table) => table.id !== payload.old.id));
          setAllTables((prevTables) => prevTables.filter((table) => table.id !== payload.old.id));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(tablesChannel);
    };
  }, [venueId]);

  return (
    <TablesContext.Provider
      value={{
        allTables: decorateTables(allTables),
        tables: decorateTables(tables),
        loading,
        floors,
        currentFloor,
        setCurrentFloor,
        selectedTable,
        orders,
        showModal,
        handleTableClick,
        handleCloseModal,
        handleDragEnd,
      }}
    >
      {children}
    </TablesContext.Provider>
  );
};

export const useTablesContext = () => useContext(TablesContext);
